// L'EFFACEUR — Panel admin Test Runner E2E

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Play, RefreshCw, CheckCircle, XCircle, Terminal, FileText, Trash2 } from 'lucide-react';
import { API } from '../../App';
import { Button } from '../ui/button';

const TestRunnerPanel = () => {
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [config, setConfig] = useState({
    skip_audit: false,
    skip_demenagement: false,
    skip_heritage: false,
    base_url: '',  // vide → utilise default backend
  });

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/test-runner/runs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data.runs || []);
    } catch {
      toast.error('Erreur chargement historique tests');
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const launchTest = async () => {
    setRunning(true);
    setLastResult(null);
    try {
      const token = localStorage.getItem('token');
      const body = {
        skip_audit: config.skip_audit,
        skip_demenagement: config.skip_demenagement,
        skip_heritage: config.skip_heritage,
      };
      if (config.base_url) body.base_url = config.base_url;
      const res = await axios.post(`${API}/admin/test-runner/run`, body, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 360000,
      });
      setLastResult(res.data);
      if (res.data.status === 'success') {
        toast.success('Tests E2E réussis');
      } else {
        toast.error(`Tests échoués (exit ${res.data.exit_code})`);
      }
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur exécution tests');
    } finally {
      setRunning(false);
    }
  };

  const cleanup = async () => {
    if (!window.confirm('Supprimer toutes les données de test (user E2E + missions taguées) ? Action irréversible.')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/test-runner/cleanup`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data.deleted;
      toast.success(`Nettoyé : ${d.users} users, ${d.missions} missions, ${d.documents} docs`);
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur cleanup');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 bg-success rounded-full" style={{ boxShadow: '0 0 10px rgba(0,255,148,0.8)' }} />
          <h2 className="font-orbitron text-xl font-black text-white">Test Runner E2E</h2>
          <span className="font-mono text-xs text-success/60">// QA · 3 PACKS</span>
        </div>
        <div className="flex gap-2">
          <Button onClick={cleanup} variant="outline" className="text-xs text-red-400 border-red-400/40 hover:bg-red-400/10">
            <Trash2 className="w-3 h-3 mr-1" /> Nettoyer données test
          </Button>
          <Button onClick={fetchHistory} variant="outline" className="text-xs">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="glass-card border border-gray-800 p-6 space-y-4">
        <h3 className="text-white font-bold mb-2">Configuration du test</h3>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={!config.skip_audit}
              onChange={(e) => setConfig({ ...config, skip_audit: !e.target.checked })}
              className="accent-cyan-400" />
            Pack Audit (upload relevé CSV → audit → résiliation)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={!config.skip_demenagement}
              onChange={(e) => setConfig({ ...config, skip_demenagement: !e.target.checked })}
              className="accent-cyan-400" />
            Pack Déménagement (formulaire + 5 contrats + mandat)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={!config.skip_heritage}
              onChange={(e) => setConfig({ ...config, skip_heritage: !e.target.checked })}
              className="accent-cyan-400" />
            Pack Héritage (acte de décès + 8 contrats à clôturer)
          </label>
        </div>
        <div>
          <label className="block text-xs font-mono text-gray-400 uppercase mb-1">Base URL (optionnel — vide = backend par défaut)</label>
          <input
            type="text"
            value={config.base_url}
            onChange={(e) => setConfig({ ...config, base_url: e.target.value })}
            placeholder="http://localhost:8000"
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:border-cyan-400 focus:outline-none"
          />
        </div>
        <Button
          onClick={launchTest}
          disabled={running}
          className="w-full bg-success text-black hover:bg-green-300 font-bold"
        >
          <Play className="w-4 h-4 mr-2" /> {running ? 'Exécution... (jusqu\'à 5 min)' : 'Lancer les tests E2E'}
        </Button>
      </div>

      {lastResult && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className={`glass-card border p-6 ${lastResult.status === 'success' ? 'border-success/40' : 'border-red-400/40'}`}>
          <div className="flex items-center gap-3 mb-4">
            {lastResult.status === 'success'
              ? <CheckCircle className="w-8 h-8 text-success" />
              : <XCircle className="w-8 h-8 text-red-400" />}
            <div>
              <h3 className="text-white font-bold">
                {lastResult.status === 'success' ? 'Tests passés' : `Tests échoués (exit ${lastResult.exit_code})`}
              </h3>
              <p className="text-xs text-gray-500 font-mono">{lastResult.report_file}</p>
            </div>
          </div>
          {lastResult.report_excerpt && (
            <details className="text-xs">
              <summary className="cursor-pointer text-cyan-400 mb-2 flex items-center gap-1">
                <FileText className="w-3 h-3" /> Rapport détaillé (markdown)
              </summary>
              <pre className="whitespace-pre-wrap bg-gray-900 border border-gray-800 rounded p-3 text-gray-300 max-h-96 overflow-y-auto">
                {lastResult.report_excerpt}
              </pre>
            </details>
          )}
          {lastResult.stderr_tail && (
            <details className="text-xs mt-2">
              <summary className="cursor-pointer text-red-400 mb-2 flex items-center gap-1">
                <Terminal className="w-3 h-3" /> stderr
              </summary>
              <pre className="whitespace-pre-wrap bg-gray-900 border border-red-400/30 rounded p-3 text-red-400 max-h-48 overflow-y-auto">
                {lastResult.stderr_tail}
              </pre>
            </details>
          )}
        </motion.div>
      )}

      <div className="glass-card border border-gray-800 overflow-hidden">
        <div className="bg-gray-900/80 p-4 border-b border-gray-800">
          <h3 className="text-white font-bold">Historique des exécutions ({history.length})</h3>
        </div>
        {loadingHistory ? (
          <div className="p-4 text-gray-500 text-sm">Chargement...</div>
        ) : history.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">Aucun test exécuté pour l'instant</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-900/50">
              <tr>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Run ID</th>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Démarré</th>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Déclenché par</th>
                <th className="text-center p-3 text-gray-500 font-mono text-xs">Exit</th>
                <th className="text-left p-3 text-gray-500 font-mono text-xs">Rapport</th>
              </tr>
            </thead>
            <tbody>
              {history.map(run => (
                <tr key={run.run_id} className="border-t border-gray-800/50">
                  <td className="p-3 text-cyan-400 font-mono text-xs">{run.run_id}</td>
                  <td className="p-3 text-gray-400 text-xs">{run.started_at?.slice(0, 19)?.replace('T', ' ')}</td>
                  <td className="p-3 text-gray-300 text-xs">{run.triggered_by}</td>
                  <td className="p-3 text-center">
                    <span className={run.exit_code === 0 ? 'text-success font-bold' : 'text-red-400 font-bold'}>
                      {run.exit_code}
                    </span>
                  </td>
                  <td className="p-3 text-gray-500 text-xs">{run.report_file || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TestRunnerPanel;
